import { motion, AnimatePresence } from 'framer-motion';
import { X, GraduationCap, Users, BookOpen, CheckCircle, Target } from 'lucide-react';

interface FormationSolutionsAffairesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FormationSolutionsAffairesModal({ isOpen, onClose }: FormationSolutionsAffairesModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-amber-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-amber-500/30 shadow-2xl shadow-amber-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-amber-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl">
                    <GraduationCap className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                      Formation & Sensibilisation Cybersécurité
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Réduction des risques humains et renforcement de la culture sécuritaire
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-amber-600/20 via-orange-600/20 to-amber-600/20 rounded-xl p-8 border-2 border-amber-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  La Formation : Essentielle
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  La meilleure technologie du monde ne sert à rien si les gens ne comprennent pas les risques. 
                  Nous devons former, sensibiliser, répéter. C'est <strong className="text-amber-400">continu</strong>.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-6 border border-amber-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Users className="w-7 h-7 text-amber-400" />
                    Programmes Adaptés
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Nous développons des programmes de formation adaptés aux rôles, aux risques, et aux besoins :
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-amber-400 font-bold mb-2">Formation Générale</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Sensibilisation aux risques</li>
                        <li>• Bonnes pratiques de base</li>
                        <li>• Reconnaissance des menaces</li>
                        <li>• Réponse aux incidents</li>
                      </ul>
                    </div>
                    <div className="bg-slate-800/50 rounded-lg p-4">
                      <h5 className="text-orange-400 font-bold mb-2">Formation Spécialisée</h5>
                      <ul className="space-y-1 text-sm text-gray-300">
                        <li>• Formation pour équipes TI</li>
                        <li>• Formation pour gestionnaires</li>
                        <li>• Formation pour utilisateurs finaux</li>
                        <li>• Formation technique avancée</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <BookOpen className="w-7 h-7 text-orange-400" />
                    Contenu de Formation
                  </h4>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Reconnaissance des menaces</strong> - Phishing, malware, ingénierie sociale</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Bonnes pratiques</strong> - Mots de passe, authentification, données sensibles</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Réponse aux incidents</strong> - Que faire en cas d'incident</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Conformité</strong> - Loi 25, protection des données</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Target className="w-7 h-7 text-yellow-400" />
                    Suivi et Documentation
                  </h4>
                  <p className="text-gray-300 mb-4">
                    La formation doit être <strong className="text-white">documentée</strong> pour la conformité :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span>Registre des formations dispensées</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span>Suivi de qui a été formé, sur quoi, quand</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span>Évaluations et certifications</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span>Formation continue et mise à jour</span>
                    </li>
                  </ul>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-amber-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-200 font-semibold shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

